import React, { useState } from "react";
import { api } from "../../lib/api";
import { X, MessageSquare, AlertTriangle, CheckCircle, Send } from "lucide-react";

export default function MessageOfficeModal({ visit, client, onClose, onSuccess }) {
  const [message, setMessage] = useState("");
  const [isUrgent, setIsUrgent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    if (!message.trim()) {
      setError("Please enter a message");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await api.post("/evv/message-office", {
        visit_id: visit?.id || null,
        client_id: client?.id || visit?.client_id || null,
        message: message.trim(),
        is_urgent: isUrgent,
      });
      
      setSuccess(true);
      setTimeout(() => {
        onSuccess?.();
        onClose();
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to send message");
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-4">
        <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-md p-6 text-center animate-in slide-in-from-bottom duration-300">
          <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-emerald-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Message Sent</h3>
          <p className="text-gray-500 mt-2">
            The office will respond as soon as possible.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50">
      <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto animate-in slide-in-from-bottom duration-300">
        {/* Header */}
        <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#06372B]/10 flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-[#06372B]" />
            </div>
            <div>
              <h2 className="font-semibold text-gray-900">Message Office</h2>
              <p className="text-sm text-gray-500">Send a secure message</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
            data-testid="close-message-modal"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {error && (
            <div className="flex items-start gap-3 p-3 rounded-xl bg-red-50 text-red-700">
              <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {/* Context info */}
          {(visit || client) && (
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="text-sm text-gray-500">Regarding</div>
              <div className="font-medium text-gray-900">
                {visit?.client_name || (client ? `${client.first_name} ${client.last_name}` : "General")}
              </div>
            </div>
          )}

          {/* Message input */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Your message
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Type your message here..."
              rows={5}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#06372B] focus:ring-1 focus:ring-[#06372B] outline-none transition-colors resize-none text-sm"
              data-testid="message-input"
              autoFocus
            />
          </div>

          {/* Urgent toggle */}
          <label className="flex items-center gap-3 p-4 rounded-xl border border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors">
            <input
              type="checkbox"
              checked={isUrgent}
              onChange={(e) => setIsUrgent(e.target.checked)}
              className="w-5 h-5 rounded border-gray-300 text-red-600 focus:ring-red-500"
              data-testid="urgent-checkbox"
            />
            <div>
              <div className="font-medium text-gray-900 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-500" />
                Mark as urgent
              </div>
              <div className="text-sm text-gray-500">
                Office will be notified immediately
              </div>
            </div>
          </label>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white px-6 py-4 border-t border-gray-100">
          <button
            onClick={handleSubmit}
            disabled={loading || !message.trim()}
            className="w-full py-3.5 px-4 rounded-xl bg-[#06372B] text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#0a4a3a] transition-colors flex items-center justify-center gap-2"
            data-testid="send-message-btn"
          >
            <Send className="w-4 h-4" />
            {loading ? "Sending..." : "Send Message"}
          </button>
        </div>
      </div>
    </div>
  );
}
